import React from "react";
import { useWM } from "./store";
import { useIsMobile } from "./useIsMobile";

/**
 * TabView — renders every open app full-screen, but only the focused one is visible.
 * All other tabs stay mounted (display:none) so their state survives tab switches —
 * exactly like browser tabs.
 */
export default function TabView() {
  const { windows, focused } = useWM();
  const isMobile = useIsMobile();
  if (!windows || windows.length === 0) return null;

  // Pick the window to display.
  //   1. The focused window IF it's not minimized.
  //   2. Otherwise, the most recently-opened non-minimized window.
  //   3. Otherwise (everything minimized), fall back to the last window so
  //      that re-asserting focus on a minimized tab still resolves an id.
  const visibleFocused = windows.find((w) => w.id === focused && !w.isMinimized);
  const fallback = [...windows].reverse().find((w) => !w.isMinimized);
  const activeId = (visibleFocused || fallback || windows[windows.length - 1]).id;

  // Mobile: no left rail, MobileNav (58 px) sits at the bottom.
  // Desktop: right auto-hide rail floats on top — no left/right reservation needed.
  // Taskbar auto-hides when any window is open, so no bottom reservation either.
  const inset = isMobile
    ? { top: 60, left: 0, right: 0, bottom: 58 }   // TopBar(28) + TabBar(32 compact)
    : { top: 66, left: 0, right: 0, bottom: 0 };

  return (
    <div
      data-testid="tab-view"
      className="absolute"
      style={{ ...inset, overflow: "hidden" }}
    >
      {windows.map((w) => {
        const Component = w.Component;
        const isActive = w.id === activeId;
        return (
          <div
            key={w.id}
            data-testid={`tab-pane-${w.appId}`}
            className="absolute inset-0 hexa-light-app"
            style={{
              display: isActive && !w.isMinimized ? "block" : "none",
              background: "#ffffff",
              overflow: "auto",
            }}
          >
            <Component {...(w.props || {})} />
          </div>
        );
      })}
    </div>
  );
}
